/* global console window $ document setTimeout */
import * as React from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import PropType from "prop-types";
import { ResizeHandleBottom, ResizeHandleLeft, ResizeHandleRight, ResizeHandleTop } from "../../assets/icons";
import { startDrag } from "../../helpers/dragAndDrop";
import { getWeights } from "../../helpers/grid";
import { resize } from "../../state/structureSlice";
import { batchGroupBy } from "../../helpers/batchGroupBy";
import { runAutofit } from "../cells/cellAutofitUtils";

const ResizeHandler = (props) => {
  const dispatch = useDispatch();
  const cells = useSelector((state) => state.gridd.present.structure.cells);
  const merged = useSelector((state) => state.gridd.present.structure.merged);
  const store = useStore();
  const compactSpacerSize = useSelector((state) => {
    const sheet = state.styleSheet?.sheets?.[state.styleSheet?.selectedSheet] ?? state.styleSheet?.sheets?.["default"];
    return sheet?.spacerWidths?.find((sw) => sw.name === "compact")?.width ?? 18;
  });

  const handlerRef = React.useRef(null);
  const lastMouseDownTimeRef = React.useRef(0);
  const [isDragging, setIsDragging] = React.useState(false);

  /**
   * Distribute totalDiffPx pixels among neighbor columns/rows.
   * Positive totalDiffPx = neighbors shrink; negative = neighbors grow.
   * Spacers are transparent — they are skipped and their size is unchanged.
   * Regular columns are clamped at colMinPx; overage is redistributed to remaining eligible columns.
   * weights[i] for spacers is a plain number (pixel value) as returned by getWeights().
   */
  const distributeResize = (neighborIndices, totalDiffPx, weights, axisArr, unit, colMinPx) => {
    const changes = {};
    // Only regular (non-spacer) columns participate
    let eligible = neighborIndices.filter((i) => axisArr[i][0] !== "s");
    for (const i of eligible) changes[i] = weights[i];

    let remaining = totalDiffPx;

    while (Math.abs(remaining) > 0.001 && eligible.length > 0) {
      const perColPx = remaining / eligible.length;
      let absorbed = 0;
      const nextEligible = [];

      for (const i of eligible) {
        const curPx = changes[i] * unit;
        const targetPx = curPx - perColPx;
        if (targetPx >= colMinPx) {
          changes[i] = changes[i] - perColPx / unit;
          absorbed += perColPx;
          nextEligible.push(i);
        } else {
          // Clamp to minimum; absorb only what's available
          const canAbsorb = Math.max(0, curPx - colMinPx);
          changes[i] = colMinPx / unit;
          absorbed += canAbsorb;
          // Column at minimum — excluded from further redistribution
        }
      }

      remaining -= absorbed;
      eligible = nextEligible;
      if (Math.abs(absorbed) < 0.001) break;
    }

    return Object.entries(changes).map(([i, weight]) => ({ index: parseInt(i), weight }));
  };

  const onDragEnd = (e, ds) => {
    setIsDragging(false);
    if (handlerRef.current) {
      $(handlerRef.current).css({ transform: "", opacity: "" });
    }
    const dsd = { ...ds, devent: { clientX: e.clientX, clientY: e.clientY } };
    console.log("drag end", dsd);
    batchGroupBy.start();
    if (ds?.content?.column !== undefined) {
      // col count: cols.length - # of vertical spacers
      // width: window.innerWidth - 48 - sum of vertical spacer widths
      // resize: if columnIndex is spacer, resize to absolute instead of weight

      const isSpacer = props.cols[dsd.content.column][0] === "s";

      let spacerSum = 0;
      props.cols
        .filter((c) => c[0] === "s")
        .forEach((c) => {
          spacerSum += parseInt(c.slice(1));
        });

      // column is being resized
      const weights = dsd.weights;
      const sumWeights = dsd.weights.reduce((sum, a, i) => (props.cols[i][0] !== "s" ? sum + a : sum), 0);
      const newWeights = [...dsd.weights];
      const diff = dsd.devent.clientX - dsd.event.clientX;
      if (diff === 0) {
        batchGroupBy.end();
        return;
      }
      const unit = (window.innerWidth - 48 - spacerSum) / sumWeights;

      if (props.left) {
        if (isSpacer) {
          const weight = "s" + Math.max(compactSpacerSize, weights[dsd.content.column] - diff);
          newWeights[dsd.content.column] = weight;
          dispatch(resize({ columnIndex: dsd.content.column, weight }));
        } else {
          const colC = weights[dsd.content.column] - diff / unit;
          newWeights[dsd.content.column] = colC;
          dispatch(resize({ columnIndex: dsd.content.column, weight: colC }));
        }

        // Distribute among ALL left neighbors; spacers are transparent, regular cols clamped at 30px
        const leftNeighbors = [];
        for (let i = 0; i < dsd.content.column; i++) leftNeighbors.push(i);
        const leftChanges = distributeResize(leftNeighbors, -diff, weights, props.cols, unit, 30);
        for (const { index, weight } of leftChanges) {
          newWeights[index] = weight;
          dispatch(resize({ columnIndex: index, weight }));
        }
        console.log("prev cols rebalanced", { col: dsd.content.column, leftNeighbors });
      } else {
        if (isSpacer) {
          const weight = "s" + Math.max(compactSpacerSize, weights[dsd.content.column] + diff);
          newWeights[dsd.content.column] = weight;
          dispatch(resize({ columnIndex: dsd.content.column, weight }));
        } else {
          const colC = weights[dsd.content.column] + diff / unit;
          newWeights[dsd.content.column] = colC;
          dispatch(resize({ columnIndex: dsd.content.column, weight: colC }));
        }

        // Distribute among ALL right neighbors; spacers are transparent, regular cols clamped at 30px
        const rightNeighbors = [];
        for (let i = dsd.content.column + 1; i < props.cols.length; i++) rightNeighbors.push(i);
        const rightChanges = distributeResize(rightNeighbors, diff, weights, props.cols, unit, 30);
        for (const { index, weight } of rightChanges) {
          newWeights[index] = weight;
          dispatch(resize({ columnIndex: index, weight }));
        }
        console.log("next cols rebalanced", { col: dsd.content.column, rightNeighbors });
      }

      console.log("column(s) resized", {
        weights,
        newWeights,
        unit,
        diff,
        widths: weights.map((w) => w * unit),
        newWidths: newWeights.map((w) => w * unit),
        window: { width: window.innerWidth, height: window.innerHeight },
        sumWeights,
        sumNewWeights: newWeights.reduce((sum, a, i) => (props.cols[i] && props.cols[i][0] !== "s" ? sum + a : sum), 0),
        spacerSum,
      });
    } else if (ds?.content?.row !== undefined) {
      const isSpacer = props.rows[dsd.content.row][0] === "s";
      let spacerSum = 0;
      props.rows
        .filter((c) => c[0] === "s")
        .forEach((c) => {
          spacerSum += parseInt(c.slice(1));
        });

      // row is being resized
      const weights = dsd.weights;
      const sumWeights = dsd.weights.reduce((sum, a, i) => (props.rows[i][0] !== "s" ? sum + a : sum), 0);
      const newWeights = [...dsd.weights];
      const diff = dsd.event.clientY - dsd.devent.clientY;
      if (diff === 0) {
        batchGroupBy.end();
        return;
      }
      const unit = (window.innerHeight - 48 - spacerSum) / sumWeights;

      if (props.top) {
        if (isSpacer) {
          const weight = "s" + Math.max(compactSpacerSize, weights[dsd.content.row] + diff);
          dispatch(resize({ rowIndex: dsd.content.row, weight }));
        } else {
          const rowC = (weights[dsd.content.row] * unit + diff) / unit;
          dispatch(resize({ rowIndex: dsd.content.row, weight: rowC }));
        }

        // Distribute among ALL top neighbors; spacers are transparent, regular rows clamped at 30px
        const topNeighbors = [];
        for (let i = 0; i < dsd.content.row; i++) topNeighbors.push(i);
        const topChanges = distributeResize(topNeighbors, diff, weights, props.rows, unit, 30);
        for (const { index, weight } of topChanges) {
          dispatch(resize({ rowIndex: index, weight }));
        }
        console.log("prev rows rebalanced", { row: dsd.content.row, topNeighbors });
      } else {
        if (isSpacer) {
          const weight = "s" + Math.max(compactSpacerSize, weights[dsd.content.row] - diff);
          dispatch(resize({ rowIndex: dsd.content.row, weight }));
        } else {
          const rowC = weights[dsd.content.row] - diff / unit;
          dispatch(resize({ rowIndex: dsd.content.row, weight: rowC }));
        }

        // Distribute among ALL bottom neighbors; spacers are transparent, regular rows clamped at 30px
        const bottomNeighbors = [];
        for (let i = dsd.content.row + 1; i < props.rows.length; i++) bottomNeighbors.push(i);
        const bottomChanges = distributeResize(bottomNeighbors, -diff, weights, props.rows, unit, 30);
        for (const { index, weight } of bottomChanges) {
          dispatch(resize({ rowIndex: index, weight }));
        }
        console.log("next rows rebalanced", { row: dsd.content.row, bottomNeighbors });
      }

      console.log("row(s) resized", {
        weights,
        newWeights,
        unit,
        diff,
        widths: weights.map((w) => w * unit),
        newWidths: newWeights.map((w) => w * unit),
        window: { width: window.innerWidth, height: window.innerHeight },
        sumWeights,
        sumNewWeights: newWeights.reduce((sum, a, i) => (props.rows[i] && props.rows[i][0] !== "s" ? sum + a : sum), 0),
        spacerSum,
      });
    }

    batchGroupBy.end();
  };

  const onDragMove = (e, obj) => {
    const { column, row } = obj.content;
    if (obj.previewElement) {
      obj.previewElement.css({
        transform:
          "translate(" +
          (column !== undefined ? e.clientX : obj.event.clientX) +
          "px, " +
          (row !== undefined ? e.clientY : obj.event.clientY) +
          "px)",
      });
    }
  };

  const onDoubleClick = (e) => {
    lastMouseDownTimeRef.current = 0;
    e.preventDefault();
    document.activeElement?.blur();
    setTimeout(() => {
      const cellOverflow = store.getState().app.cellOverflow;
      const vertical = props.left !== undefined;
      const index = vertical ? props.j : props.i;
      const preferredNeighborDirection = props.left === true || props.top === true ? -1 : +1;
      runAutofit({
        vertical,
        index,
        cols: props.cols,
        rows: props.rows,
        cells,
        merged,
        cellOverflow,
        dispatch,
        preferredNeighborDirection,
      });
    }, 0);
  };

  return (
    <div
      ref={handlerRef}
      id={`resize_${props.left ? "left" : props.left === false ? "right" : props.top ? "top" : "bottom"}_handler`}
      style={{
        position: "absolute",
        top: props.top === false ? undefined : 0,
        bottom: props.top === false ? 0 : undefined,
        left: props.top ? 0 : undefined,
        right: props.left === false ? -3 : undefined,
        width: props.left === undefined ? "calc(100vw - 58px)" : 2,
        height: props.top === undefined ? "calc(100vh - 58px)" : 2,
        zIndex: 999,
        backgroundColor: "#0065FD",
        pointerEvents: "all",
        opacity: isDragging ? 0 : 1,
      }}
      onMouseDown={(e) => {
        const now = Date.now();
        if (now - lastMouseDownTimeRef.current < 300) {
          onDoubleClick(e);
          return;
        }
        lastMouseDownTimeRef.current = now;
        console.log("resize handler mouse down", e);
        const initialRect = handlerRef.current.getBoundingClientRect();
        const column = props.top === undefined ? props.j : undefined;
        const row = props.left === undefined ? props.i : undefined;
        setIsDragging(true);
        startDrag(e, {
          content: {
            column,
            row,
            initialRect,
          },
          weights: getWeights(props.left === undefined ? props.rows : props.cols),
          previewContent: $(handlerRef.current)
            .clone()
            .attr("id", "")
            .css({
              opacity: 0.5,
              transform: "",
              position: "absolute",
              left: initialRect.left - e.clientX,
              top: initialRect.top - e.clientY,
            }),
          onDragEnd,
          onDragMove,
        });
        e.preventDefault();
      }}
    >
      {props.left ? (
        <ResizeHandleLeft
          style={{
            position: "relative",
            top: "50%",
            left: -24,
          }}
        />
      ) : props.left === false ? (
        <ResizeHandleRight
          style={{
            position: "relative",
            top: "50%",
            left: 4,
          }}
        />
      ) : props.top ? (
        <ResizeHandleTop style={{ position: "relative", left: "50%", top: -24 }} />
      ) : (
        <ResizeHandleBottom style={{ position: "relative", left: "50%", top: 4 }} />
      )}
    </div>
  );
};

ResizeHandler.propTypes = {
  i: PropType.number.isRequired,
  j: PropType.number.isRequired,
  left: PropType.bool,
  top: PropType.bool,
  cols: PropType.array,
  rows: PropType.array,
};

export default ResizeHandler;
